/**
 * useWebAuthn Hook
 * Handles WebAuthn/Passkey registration and authentication
 */
import { useState, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';

// Check if WebAuthn is supported
export const isWebAuthnSupported = () => {
  return window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential === 'function';
};

// Convert base64url to ArrayBuffer
const base64urlToBuffer = (base64url) => {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Convert ArrayBuffer to base64url
const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

export const useWebAuthn = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);

  // Fetch user's passkeys
  const fetchCredentials = useCallback(async () => {
    try {
      const response = await api.get('/passkey/list');
      setCredentials(response.data.passkeys || []);
    } catch (error) {
      console.error('Failed to fetch credentials:', error);
    }
  }, []);

  // Register a new passkey
  const registerPasskey = useCallback(async (credentialName = 'My Passkey') => {
    if (!isWebAuthnSupported()) {
      toast.error('WebAuthn is not supported in this browser');
      return false;
    }

    setIsLoading(true);
    try {
      // Start registration
      const startResponse = await api.post('/passkey/register/start', { device_name: credentialName });
      const optionsJson = startResponse.data.options;
      
      // Parse the options JSON string if needed
      const options = typeof optionsJson === 'string' ? JSON.parse(optionsJson) : optionsJson;

      // Convert options for browser API
      const publicKeyOptions = {
        challenge: base64urlToBuffer(options.challenge),
        rp: options.rp,
        user: {
          id: base64urlToBuffer(options.user.id),
          name: options.user.name,
          displayName: options.user.displayName,
        },
        pubKeyCredParams: options.pubKeyCredParams,
        timeout: options.timeout || 60000,
        attestation: options.attestation || 'none',
        authenticatorSelection: {
          residentKey: options.authenticatorSelection?.residentKey || 'preferred',
          userVerification: options.authenticatorSelection?.userVerification || 'preferred',
        },
        excludeCredentials: (options.excludeCredentials || []).map(cred => ({
          id: base64urlToBuffer(cred.id),
          type: cred.type,
          transports: cred.transports,
        })),
      };

      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions,
      });

      // Prepare response for server
      const credentialData = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          attestationObject: bufferToBase64url(credential.response.attestationObject),
          transports: credential.response.getTransports?.() || [],
        },
      };

      // Complete registration
      await api.post('/passkey/register/finish', {
        credential: credentialData,
        device_name: credentialName,
      });

      toast.success('Passkey registered successfully!');
      await fetchCredentials();
      return true;
    } catch (error) {
      console.error('Passkey registration failed:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Registration was cancelled or timed out');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to register passkey');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [fetchCredentials]);

  // Authenticate with passkey
  const loginWithPasskey = useCallback(async (identifier) => {
    if (!isWebAuthnSupported()) {
      toast.error('WebAuthn is not supported in this browser');
      return null;
    }

    setIsLoading(true);
    try {
      // Start authentication
      const startResponse = await api.post('/passkey/authenticate/start', { identifier });
      const optionsJson = startResponse.data.options;
      const userId = startResponse.data.user_id;
      
      // Parse the options JSON string if needed
      const options = typeof optionsJson === 'string' ? JSON.parse(optionsJson) : optionsJson;

      // Convert options for browser API
      const publicKeyOptions = {
        challenge: base64urlToBuffer(options.challenge),
        timeout: options.timeout || 60000,
        rpId: options.rpId,
        allowCredentials: (options.allowCredentials || []).map(cred => ({
          id: base64urlToBuffer(cred.id),
          type: cred.type,
          transports: cred.transports,
        })),
        userVerification: options.userVerification || 'preferred',
      };

      // Get credential
      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions,
      });

      // Prepare response for server
      const credentialData = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          authenticatorData: bufferToBase64url(credential.response.authenticatorData),
          signature: bufferToBase64url(credential.response.signature),
          userHandle: credential.response.userHandle 
            ? bufferToBase64url(credential.response.userHandle) 
            : null,
        },
      };

      // Complete authentication
      const authResponse = await api.post('/passkey/authenticate/finish', {
        user_id: userId,
        credential: credentialData,
      });

      toast.success('Logged in with passkey!');
      return authResponse.data;
    } catch (error) {
      console.error('Passkey authentication failed:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Authentication was cancelled or timed out');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to authenticate with passkey');
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Delete a passkey
  const deleteCredential = useCallback(async (credentialId) => {
    setIsLoading(true);
    try {
      await api.delete('/passkey/' + encodeURIComponent(credentialId));
      toast.success('Passkey deleted');
      await fetchCredentials();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete passkey');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [fetchCredentials]);

  return {
    isLoading,
    credentials,
    isSupported: isWebAuthnSupported(),
    fetchCredentials,
    registerPasskey,
    loginWithPasskey,
    deleteCredential,
  };
};

export default useWebAuthn;
